// ============================================================================
//  Aritmética de precios del checkout.
//
//  Extraído VERBATIM de components/checkout/pay2/index.jsx. El objetivo de este
//  módulo es que el cálculo de lo que se cobra se pueda testear sin montar React:
//  en MercadoPago el front manda el `unit_price` de cada item y el backend lo
//  cobra tal cual salvo que el carrito matchee un pack COMPLETO
//  (admin-back MercadoPagoController::applyPackRounding, early return si no hay
//  pack). O sea que para ARS estas funciones SON el importe cobrado.
//  En Stripe el back re-precia todo desde la DB y acá solo se decide qué se
//  muestra en pantalla.
//
//  REGLA AL TOCAR ESTE ARCHIVO: el mismo algoritmo está implementado en PHP en
//  admin-back (StripeController y MercadoPagoController). Cualquier cambio de
//  comportamiento tiene que ir a los dos lados o la pantalla deja de coincidir
//  con lo que se cobra. Los tests de __tests__/pricing.test.js pinean los
//  números con la matriz real de precios.
//
//  Todo acá es puro: sin React, sin DOM, sin i18n, sin lectura de estado. Los
//  dos steps de redondeo (originalRoundStep y packRoundStep) viajan SIEMPRE por
//  parámetro: en el componente vivían en el mismo objeto `pricingCountry` y se
//  leían por closure, que es como se coló que `totalAfterBundle` dependiera de
//  `originalRoundStep` sin declararlo.
// ============================================================================

import { PREORDER_LANGS_BY_SLUG } from "@/lib/books/preorder";
import { TODOS } from "@/lib/idiomas";

// ── Productos ──
// pages/apps: espejo de BOOK_META de home-libro2 (para los bullets de valor del
// resumen).
//
// `preorderLangs` (fuente: lib/books/preorder) = idiomas donde el libro todavía
// no se entrega. Decide dos cosas en pay2: si se muestra `preorderSubtext` (la
// fecha de disponibilidad, obligatoria en toda pantalla que ofrezca preventa) y
// si se puede prometer "acceso inmediato". Escalas ya se entregó en los SIETE
// idiomas, así que el 61 no lleva ninguna de las dos: hoy el único que las lleva
// es Música Ilustrada de Cero.
export const PRODUCT_META = {
  1:    { nameKey: "illustrated_harmony",      color: "#f1ca0d", pages: 153, apps: 2 },
  11:   { nameKey: "illustrated_modes",        color: "#f5aacb", pages: 190, apps: 1 },
  21:   { nameKey: "illustrated_harmony_2",    color: "#99c846", pages: 218, apps: 2 },
  31:   { nameKey: "illustrated_tensions",     color: "#b5e3f3", pages: 209, apps: 4 },
  41:   { nameKey: "magic_glue",               color: "#f39ea1", pages: 180, apps: 4 },
  51:   { nameKey: "illustrated_arrangements", color: "#c4a6e8", pages: 142, apps: 3 },
  61:   { nameKey: "illustrated_scales",      color: "#9288ff", pages: 154, apps: 1 },
  // Musica Ilustrada de Cero va SIN `pages` ni `apps`: el libro los va a traer pero
  // todavia no se sabe cuantos, y poner 0 no es lo mismo que no poner nada. El
  // bullet de valor del resumen esta guardado con `totPages > 0 && totApps > 0`,
  // asi que omitirlos lo esconde en vez de mostrar "0 paginas".
  71:   { nameKey: "illustrated_music_zero",  color: "#e79b45", preorderSubtext: "illustrated_music_zero_subtext", preorderLangs: PREORDER_LANGS_BY_SLUG.mic },
  1001: { nameKey: "clarinet_pack",            color: "#6dbfb8" },
};

// Los libros del "tambien podes llevar" de /checkout-pay. Musica Ilustrada de Cero entra
// aunque sea preventa: la card se dibuja con preorderSubtextFor(), que saca la fecha de
// PRODUCT_META[71].preorderSubtext y solo la muestra en los idiomas donde el libro
// todavia no salio. Sin el 71 aca, el octavo libro era el unico que no se podia sumar
// al carrito desde el checkout.
export const MAIN_IDS = [1, 11, 21, 31, 41, 51, 61, 71];

// ── Packs ──
// OJO: estos ids son los que deciden el TÍTULO y las tapas apiladas de la
// pantalla. La PLATA la decide el pack que viene de la API (pack_discounts +
// product_ids, ver matchPackDiscount). Hoy coinciden, pero son dos nociones
// independientes: si se tocan los productos de un pack en el admin, la UI puede
// decir "Pack Futuro" y aplicarse otro descuento. No unificar sin decidirlo.
export const PACK_CONFIG = {
  panorama: { ids: [1, 11, 31],         nameKey: "pack_panorama_name" },
  futuro:   { ids: [1, 11, 21, 31, 51, 61], nameKey: "pack_futuro_name" },
};

// Números canónicos de los packs (los mismos que muestran las cards de /libros:
// l2_panorama_details / l2_full_details). Las apps compartidas entre libros se
// cuentan una sola vez, por eso NO es la suma de PRODUCT_META.
export const PACK_VALUE = {
  panorama: { pages: 550, apps: 6 },
  futuro:   { pages: 1066, apps: 9 },
};

// Detectar pack contenido en los IDs seleccionados (el más grande que matchee)
// Retorna { packKey, extraIds } o null
export const detectPack = (ids) => {
  const idSet = new Set(ids);
  // Probar del pack más grande al más chico
  const sorted = Object.entries(PACK_CONFIG).sort((a, b) => b[1].ids.length - a[1].ids.length);
  for (const [packKey, pack] of sorted) {
    if (pack.ids.every((id) => idSet.has(id))) {
      const extraIds = ids.filter((id) => !pack.ids.includes(id));
      return { packKey, extraIds };
    }
  }
  return null;
};

// El pack que se DIBUJA cuando la persona declaró libros que ya tiene. detectPack mira
// solo el carrito: con Escalas ya comprada y los otros cinco del Futuro en el carrito
// dibujaba "Pack Panorama + 2 sueltos" con los dos a precio de lista, mientras el total
// (que matchea con carrito + owned) les aplicaba el descuento del Futuro a los cinco.
// Las líneas no sumaban el total y el comprador creyó que a esos dos no les tocaba el
// pack. Acá el pack se busca en carrito + owned y los extras son solo del carrito. Hacen
// falta al menos DOS libros del carrito en ese pack: uno solo no es un pack, es un libro
// a precio de pack, y se sigue dibujando como libro.
export const detectPackConPoseidos = (selectedIds, ownedIds = []) => {
  const soloCarrito = detectPack(selectedIds);
  const poseidos = (ownedIds || []).filter((id) => !selectedIds.includes(id));
  if (!poseidos.length) return soloCarrito;
  const conPoseidos = detectPack([...selectedIds, ...poseidos]);
  if (!conPoseidos) return soloCarrito;
  const packIds = PACK_CONFIG[conPoseidos.packKey].ids;
  if (new Set(selectedIds.filter((id) => packIds.includes(id))).size < 2) return soloCarrito;
  return { packKey: conPoseidos.packKey, extraIds: selectedIds.filter((id) => !packIds.includes(id)) };
};

// ── Imágenes por idioma ──
export const COVER_IMAGES = {
  1:    (lang) => `/book/${lang}/tapa_armonia_ilustrada.jpg`,
  11:   (lang) => `/book/${lang}/tapa_modos_ilustrados.jpg`,
  21:   (lang) => `/book/${lang}/tapa_armonia_ilustrada_2.jpg`,
  31:   (lang) => `/book/${lang}/tapa_tensiones_ilustradas.jpg`,
  41:   (lang) => `/book/${lang}/tapa_glue_magico_para_guitarristas.jpg`,
  51:   (lang) => `/book/${lang}/tapa_arreglos_ilustrados.jpg`,
  61:   (lang) => `/book/${lang}/tapa_escalas_ilustradas.png`,
  71:   (lang) => `/book/${lang}/tapa_musica_ilustrada_de_cero-v2.png`,
  1001: ()     => `/clarinet/poster.jpeg`,
};

export const coverFor = (id, lang) => {
  const fn = COVER_IMAGES[id];
  return fn ? fn(lang || "es") : "/book/book-hero.jpg";
};

// ── País / idioma ──

export const EUROPE_ISO_SET = new Set([
  "AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR",
  "HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK",
  "SI","ES","SE","NO","IS","AX","AD","MC","SM","VA","ME","BA","MK","XK",
]);

export const codeEq = (a, b) => String(a || "").toUpperCase() === String(b || "").toUpperCase();

export const FALLBACK_LOCALE_BY_LANG = { es: "es-ES", en: "en-US", pt: "pt-BR", fr: "fr-FR", de: "de-DE", it: "it-IT", ja: "ja-JP", tr: "tr-TR" };

// Todos, ocultos incluidos (lib/idiomas.js): el idioma del sitio es la edición que se vende.
export const SUPPORTED_LANGS = TODOS;

export const normalizeLang = (l) => {
  const x = String(l || "").toLowerCase().split("-")[0];
  return SUPPORTED_LANGS.includes(x) ? x : "es";
};

const isAlpha2 = (cc) => /^[A-Z]{2}$/.test(cc || "");

// No es puro en sentido estricto: hace feature-detect contra el ICU del runtime
// (Node y el navegador pueden diferir). Corre dentro de un effect, nunca en render.
export function resolveLocaleFallback(countryCode, lang) {
  const CC = String(countryCode || "").toUpperCase();
  if (isAlpha2(CC)) {
    const tag = `und-${CC}`;
    try { new Intl.NumberFormat(tag).format(1.1); return tag; } catch {}
  }
  return FALLBACK_LOCALE_BY_LANG[lang] || "en-US";
}

// ── Selección de productos ──

// Es el Pack Futuro: el checkout sin ?products= tiene que armar el pack entero. Cuando
// entro Escalas al pack esta lista quedo en cinco y el carrito por defecto dejo de
// matchear Futuro; caia en Panorama y ofrecia 10,9% en vez de 21,6%.
export const DEFAULT_IDS = [1, 11, 21, 31, 51, 61];

// OJO: no deduplica. ?products=1,1 mete el libro dos veces y lo suma dos veces.
export const parseProducts = (param) => {
  if (!param || !param.trim()) return [...DEFAULT_IDS];
  return param.split(",").map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n));
};

// Libros ya poseídos ("completar colección"): NO se cobran, solo califican el
// descuento de pack. El backend los re-verifica contra las ventas del email
// (OwnedBooks::verify): si el mail del checkout no los compró, el pack NO
// matchea server-side y se cobra lista.
export const parseOwned = (param, selectedIds = []) => {
  return String(param || "")
    .split(",")
    .map((s) => parseInt(s, 10))
    .filter((n) => Number.isFinite(n) && n > 0 && !selectedIds.includes(n));
};

// ── Formato ──

// DEBE ESPEJAR StripeController::currencyExponent. Ojo con HUF, ISK, TWD y UGX:
// son zero-decimal para payouts, pero Stripe las cobra como monedas de 2
// decimales. Tenerlas acá en 0 hacía que la pantalla mostrara un entero y Stripe
// cobrara con centavos.
export const currencyExponent = (c) => {
  const C = String(c || "").toUpperCase();
  if (["BIF","CLP","DJF","GNF","JPY","KMF","KRW","MGA","PYG","RWF","VND","VUV","XAF","XOF","XPF"].includes(C)) return 0;
  if (["BHD","JOD","KWD","OMR","TND","LYD"].includes(C)) return 3;
  return 2;
};

// Cuantiza a la unidad mínima de la moneda, igual que el PHP:
//   round($v, $exp === 0 ? 0 : 2)
// Las de 3 decimales se tratan como 2, tal cual hace el backend.
export const quantizeToCurrency = (value, exponent) => {
  const v = Number(value);
  if (!Number.isFinite(v)) return v;
  return exponent === 0 ? Math.round(v) : Number(v.toFixed(2));
};

export const formatNumber = (value, currency, displayLocale) =>
  typeof value === "number" && !Number.isNaN(value)
    ? new Intl.NumberFormat(displayLocale, {
        minimumFractionDigits: currencyExponent(currency),
        maximumFractionDigits: currencyExponent(currency),
      }).format(value)
    : "—";

// OJO: Number(null) === 0, así que un precio null se imprime "0" y no "—".
export const formatPrice = (v, currency, displayLocale) => formatNumber(Number(v), currency, displayLocale);

// ── Redondeo ──
// Dos motores opuestos: roundToStep va al MÁS CERCANO (precio original tachado)
// y roundPackPrice va HACIA ARRIBA, con floor solo si el ceil no llega al
// target_discount prometido por el pack.

const decimalsIn = (n) => { const str = String(n); const i = str.indexOf("."); return i === -1 ? 0 : str.length - i - 1; };

export const roundToStep = (value, step) => {
  const v = Number(value); const s = Number(step);
  if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
  const scale = Math.pow(10, decimalsIn(s));
  const V = Math.round(v * scale); const S = Math.round(s * scale);
  return Math.round(V / S) * S / scale;
};

// `maxTotal` es lo que costarían esos mismos libros SIN el descuento de pack.
// Es el techo: un pack nunca puede salir más caro que comprar suelto.
export const roundPackPrice = (value, step, originalTotal, targetDiscount, maxTotal) => {
  const v = Number(value); const s = Number(step);
  if (!Number.isFinite(v) || !Number.isFinite(s) || s <= 0) return value;
  const precision = decimalsIn(s);
  const ceiled  = parseFloat((Math.ceil(v / s) * s).toFixed(precision));
  const floored = parseFloat((Math.floor(v / s) * s).toFixed(precision));
  let rounded = ceiled;
  if (targetDiscount != null && Number.isFinite(originalTotal) && originalTotal > 0) {
    const ceilDiscPct = (1 - ceiled / originalTotal) * 100;
    if (ceilDiscPct < targetDiscount) rounded = floored;
  }
  // Techo: si el ceil se pasa de lo que costaría sin pack, va el floor. Sin esto,
  // al completar la colección en los países donde el step pesa mucho contra el
  // precio del libro (IL, PE, HU, CO), declarar los libros que ya tenés salía MÁS
  // CARO que no declararlos: en IL un libro de lista 69 terminaba en 70.
  if (Number.isFinite(maxTotal) && maxTotal > 0 && rounded > maxTotal) rounded = floored;
  // Nunca dejar el subtotal del pack en 0 (libro "gratis") cuando había monto.
  // Espeja el guard que ya tenían los dos controladores PHP.
  if (v > 0 && rounded < s) rounded = s;
  return rounded;
};

// Precio de LISTA para mostrar tachado. Se redondea a originalRoundStep para que
// quede un número lindo. OJO: este NO es el que decide el gate de
// target_discount, para eso está chargeOriginal (ver computePackRounding).
export const computeOriginal = (price, discountPct, originalRoundStep) => {
  const d = Number(discountPct);
  if (Number.isFinite(price) && price > 0 && Number.isFinite(d) && d > 0 && d < 100) {
    const raw = price / (1 - d / 100);
    const step = Number(originalRoundStep);
    return Number.isFinite(step) && step > 0 ? roundToStep(raw, step) : raw;
  }
  return null;
};

// Precio de lista tal como lo calcula el BACKEND para el gate de target_discount:
// sin originalRoundStep, solo cuantizado a la moneda.
//   PHP: $localOrig = $localPrice / (1 - $indivDisc/100);  round($localOrig * $mult)
// Usar el redondeado (computeOriginal) movía el porcentaje lo suficiente como
// para cruzar el gate y elegir floor donde el backend elegía ceil: en JP eran
// 100 yenes de diferencia entre lo que mostraba la pantalla y lo que cobraba
// Stripe.
export const chargeOriginal = (price, discountPct, exponent) => {
  const p = Number(price);
  const d = Number(discountPct);
  if (!Number.isFinite(p) || p <= 0) return 0;
  const raw = Number.isFinite(d) && d > 0 && d < 100 ? p / (1 - d / 100) : p;
  return quantizeToCurrency(raw, exponent);
};

// ── Resolución de país / moneda / precios ──
// Cadena de fallback (EL ORDEN IMPORTA): code exacto -> EU si es europeo ->
// RW (USD) -> el país de menor id. PL/CZ/SE/HU/RO están en EUROPE_ISO_SET y
// además tienen fila propia, así que resuelven a SU moneda porque el match
// exacto va primero.
export function resolvePricing({ countryCode, pricesMatrix, language }) {
  const empty = {
    chosenCountry: null,
    currency: "USD",
    priceMap: {},
    discounts: {},
    displayLocale: resolveLocaleFallback(countryCode, language),
  };

  const rawCountries = pricesMatrix?.countries ?? [];
  const products = pricesMatrix?.products ?? [];
  const prices = pricesMatrix?.prices ?? [];
  const countries = rawCountries.map((c) => ({
    ...c,
    code: String(c.code || "").toUpperCase(),
    currencyCode: String(c.currencyCode || "").toUpperCase(),
    originalRoundStep: c.originalRoundStep ?? c.original_round_step ?? null,
    packRoundStep: c.packRoundStep ?? c.pack_round_step ?? null,
    displayLocale: c.displayLocale ?? c.display_locale ?? null,
  }));
  if (!countries.length) return empty;

  const byCode = {};
  for (const c of countries) byCode[c.code] = c;
  const cc = String(countryCode || "").toUpperCase();
  let base = byCode[cc] || null;
  if (!base && EUROPE_ISO_SET.has(cc)) base = byCode["EU"] || null;
  if (!base) base = byCode["RW"] || null;
  if (!base) base = [...countries].sort((a, b) => (a.id ?? 0) - (b.id ?? 0))[0];

  const discounts = {};
  for (const p of products) {
    const d = Number(p?.discount);
    discounts[p.id] = Number.isFinite(d) ? d : 0;
  }

  const priceMap = {};
  for (const row of prices) {
    if (Number(row.countryId) === Number(base.id)) {
      priceMap[row.productId] = row.price == null ? null : Number(row.price);
    }
  }

  return {
    chosenCountry: base,
    currency: base.currencyCode || "USD",
    priceMap,
    discounts,
    displayLocale: base.displayLocale ? String(base.displayLocale) : resolveLocaleFallback(base.code, language),
  };
}

// ── Descuento de pack ──
// Gana el pack con mayor discount_pct entre los que están CONTENIDOS en el set
// de match (carrito + owned). Number() en el borde a propósito: la columna es
// decimal(5,2) y si algún día se cae el cast del modelo Laravel, comparar
// strings elegiría "9.00" > "23.30".
export function matchPackDiscount(packDiscounts, idSet) {
  if (!packDiscounts?.length || !idSet?.size) return null;
  let best = null;
  for (const pack of packDiscounts) {
    const pids = pack.product_ids || [];
    if (pids.length > 0 && pids.every((id) => idSet.has(id))) {
      if (!best || Number(pack.discount_pct) > Number(best.discount_pct)) {
        best = pack;
      }
    }
  }
  return best;
}

// ── Redondeo del pack: UNA sola implementación ──
//
// El subtotal del pack se calculaba en tres lugares (totales de pantalla,
// subtotal del pack e items para MercadoPago) y uno de los tres sumaba sin
// cuantizar por item: en AU la pantalla decía 80 y Stripe cobraba 79. Ahora los
// tres pasan por acá, así que no puede volver a divergir.
//
// El target_discount es la promesa del pack COMPLETO ("30% off llevando los 5").
// Aplicarlo a un subtotal de un solo libro (completar colección) no tenía
// sentido: con un step que pesa 13-21% del precio del item, el floor regalaba
// hasta 33% y dos libros de precio parecido salían invertidos (en IL el de lista
// 75 salía 50 y el de 69 salía 60). Los `owned` siguen habilitando el descuento
// del pack, pero no la promesa del target.
export const packCompletoEnCarrito = (packIds, cartIds) =>
  !!packIds?.size && [...packIds].every((id) => cartIds.has(id));

export function computePackRounding({ packUnitPrices, packOriginals, packRoundStep, targetDiscount, packMaxTotal }) {
  const raw = (packUnitPrices || []).reduce((s, v) => s + v, 0);
  const step = Number(packRoundStep);
  if (!Number.isFinite(step) || step <= 0 || raw <= 0) return { raw, rounded: raw };
  const origTotal = (packOriginals || []).reduce((s, v) => s + v, 0);
  return { raw, rounded: roundPackPrice(raw, step, origTotal, targetDiscount, packMaxTotal) };
}

// Precio de cada item del pack ya con el descuento y cuantizado a la moneda, su
// precio de lista para el gate, y el precio SIN descuento de pack para el techo.
// Es lo que consumen los tres caminos.
function packLines(items, packIds, pct, exponent, discounts) {
  const mult = 1 - pct / 100;
  const unitPrices = [];
  const originals = [];
  let maxTotal = 0;
  for (const it of items) {
    if (!packIds.has(it.id)) continue;
    const price = Number.isFinite(Number(it.price)) ? Number(it.price) : 0;
    unitPrices.push(quantizeToCurrency(price * mult, exponent));
    originals.push(chargeOriginal(price, discounts[it.id] || 0, exponent));
    maxTotal += price;
  }
  return { unitPrices, originals, maxTotal };
}

// ── Totales de pantalla ──
// EL ORDEN IMPORTA: (1) multiplicador de pack por item CUANTIZADO a la moneda,
// (2) suma del subtotal de pack, (3) redondeo de ESE subtotal, (4) recién ahí se
// suman los extras sin redondear. Redondear el gran total en vez del subtotal
// cambia lo que se cobra.
export function computeTotals({
  cartItems,
  packProductIds,
  packDiscountPct,
  targetDiscount,
  packRoundStep,
  originalRoundStep,
  discounts,
  currency,
}) {
  const items = cartItems || [];
  const packIds = packProductIds || new Set();
  const pct = Number(packDiscountPct) || 0;
  const disc = discounts || {};
  const exponent = currencyExponent(currency);

  const totalNumber = items.reduce(
    (sum, it) => sum + (Number.isFinite(Number(it.price)) ? Number(it.price) : 0),
    0,
  );

  const totalOriginalNumber = items.reduce((sum, it) => {
    const price = Number(it.price);
    if (!Number.isFinite(price) || price <= 0) return sum;
    const original = computeOriginal(price, disc[it.id] || 0, originalRoundStep);
    return sum + (original != null && original > price ? original : price);
  }, 0);

  let totalAfterBundle;
  if (!pct || totalNumber <= 0) {
    totalAfterBundle = totalNumber;
  } else {
    const extraSubtotal = items.reduce(
      (sum, it) => (packIds.has(it.id) ? sum : sum + (Number.isFinite(Number(it.price)) ? Number(it.price) : 0)),
      0,
    );
    const { unitPrices, originals, maxTotal } = packLines(items, packIds, pct, exponent, disc);
    const gate = packCompletoEnCarrito(packIds, new Set(items.map((it) => it.id))) ? targetDiscount : null;
    const { rounded } = computePackRounding({
      packUnitPrices: unitPrices,
      packOriginals: originals,
      packRoundStep,
      targetDiscount: gate,
      packMaxTotal: maxTotal,
    });
    totalAfterBundle = rounded + extraSubtotal;
  }

  const hasAnyDiscount = totalOriginalNumber > totalAfterBundle;
  const totalDiscountPct =
    totalOriginalNumber <= 0 || totalAfterBundle <= 0
      ? 0
      : Math.round((1 - totalAfterBundle / totalOriginalNumber) * 100);

  return { totalNumber, totalAfterBundle, totalOriginalNumber, hasAnyDiscount, totalDiscountPct };
}

// Subtotal SOLO del pack, para cuando además hay libros sueltos en el carrito.
// Filtra por PACK_CONFIG (la noción visual de pack) pero aplica el descuento por
// packProductIds (la noción de la API): son dos sets distintos a propósito.
export function computePackSubtotals({
  packKey,
  extraIds,
  cartItems,
  packProductIds,
  packDiscountPct,
  targetDiscount,
  packRoundStep,
  originalRoundStep,
  discounts,
  currency,
}) {
  if (!packKey || !extraIds?.length) return null;
  const packIds = packProductIds || new Set();
  const pct = Number(packDiscountPct) || 0;
  const disc = discounts || {};
  const exponent = currencyExponent(currency);

  const packItems = (cartItems || []).filter((it) => PACK_CONFIG[packKey].ids.includes(it.id));
  // Los items del pack visual que además están en el pack de la API llevan
  // descuento; el resto va a precio de lista. Mismo criterio de cuantización que
  // computeTotals para que el subtotal mostrado y el gran total no se peleen.
  let packFinal = packItems.reduce((sum, it) => {
    const price = Number.isFinite(Number(it.price)) ? Number(it.price) : 0;
    return sum + (packIds.has(it.id) && pct > 0 ? quantizeToCurrency(price * (1 - pct / 100), exponent) : price);
  }, 0);
  const packOriginal = packItems.reduce((sum, it) => {
    const price = Number(it.price);
    if (!Number.isFinite(price) || price <= 0) return sum;
    const original = computeOriginal(price, disc[it.id] || 0, originalRoundStep);
    return sum + (original != null && original > price ? original : price);
  }, 0);
  // Apply pack rounding
  const packStep = Number(packRoundStep);
  if (Number.isFinite(packStep) && packStep > 0) {
    const gate = packCompletoEnCarrito(packIds, new Set((cartItems || []).map((it) => it.id)))
      ? targetDiscount
      : null;
    const packOrigParaGate = packItems.reduce(
      (sum, it) => (packIds.has(it.id) ? sum + chargeOriginal(Number(it.price), disc[it.id] || 0, exponent) : sum),
      0,
    );
    const packMaxTotal = packItems.reduce((sum, it) => sum + (Number(it.price) || 0), 0);
    packFinal = roundPackPrice(packFinal, packStep, packOrigParaGate, gate, packMaxTotal);
  }
  const packDiscTotal = packOriginal > packFinal ? Math.round((1 - packFinal / packOriginal) * 100) : 0;
  return { final: packFinal, original: packOriginal, discountPct: packDiscTotal, hasDiscount: packOriginal > packFinal };
}

// ── Items para MercadoPago ──
// ESTO ES LO QUE SE COBRA. El backend solo re-precia si el carrito matchea un
// pack completo; si no, manda estos unit_price tal cual a MP.
//
// El resto del redondeo se lo come el ÚLTIMO item del pack, y el orden de los
// items es el del parámetro ?products= de la URL: los unit_price individuales
// dependen del orden de la query string (el total no).
export function applyPackDiscountToItems(items, pct, packIds, { packRoundStep, discounts, targetDiscount, currency } = {}) {
  const ids = packIds || new Set();
  const disc = discounts || {};
  const exponent = currencyExponent(currency);
  const mult = pct && pct > 0 ? Math.max(0, 1 - pct / 100) : 1;
  const result = (items || []).map((it) => {
    const price = Number(it.unit_price || it.price || 0);
    const applyDiscount = pct > 0 && ids.has(it.id);
    return { ...it, unit_price: quantizeToCurrency(price * (applyDiscount ? mult : 1), exponent) };
  });

  // Apply pack rounding (distributed across pack items)
  // Wrapped in try/catch: if anything fails, return items with pack discount but without rounding
  // (the backend will apply pack rounding server-side as a safety net)
  try {
    const packStep = Number(packRoundStep);
    if (pct > 0 && Number.isFinite(packStep) && packStep > 0) {
      const packItems = result.filter((it) => ids.has(it.id));
      const rawPackTotal = packItems.reduce((s, it) => s + Number(it.unit_price), 0);
      if (rawPackTotal > 0) {
        // El original del gate se calcula sobre los precios PRE descuento de pack
        // (los que vienen en `items`) y sin originalRoundStep, igual que el PHP.
        const packOrigTotal = (items || []).reduce((s, it) => {
          if (!ids.has(it.id)) return s;
          const prePackPrice = Number(it.unit_price || it.price || 0);
          return s + chargeOriginal(prePackPrice, disc[it.id] || 0, exponent);
        }, 0);

        const gate = packCompletoEnCarrito(ids, new Set((items || []).map((it) => it.id)))
          ? targetDiscount
          : null;
        // Techo: lo que costarían esos mismos libros sin descuento de pack.
        const packMaxTotal = (items || []).reduce(
          (s, it) => (ids.has(it.id) ? s + Number(it.unit_price || it.price || 0) : s),
          0,
        );
        const roundedPackTotal = roundPackPrice(rawPackTotal, packStep, packOrigTotal, gate, packMaxTotal);
        const diff = rawPackTotal - roundedPackTotal;
        if (Math.abs(diff) > 0.001) {
          const absDiff = Math.abs(diff);
          const sign = diff > 0 ? 1 : -1;
          // El reparto va en la unidad mínima de la moneda, igual que el PHP
          // (que trabaja en centavos enteros). En JPY eso es el yen: con 2
          // decimales fijos quedaban unit_price con fracción de yen.
          const f = exponent === 0 ? 1 : 100;
          let distributed = 0;
          for (let i = 0; i < packItems.length; i++) {
            const it = packItems[i];
            let adj;
            if (i === packItems.length - 1) {
              adj = quantizeToCurrency(absDiff - distributed, exponent);
            } else {
              adj = quantizeToCurrency(Math.floor((absDiff * it.unit_price / rawPackTotal) * f) / f, exponent);
            }
            distributed += adj;
            it.unit_price = quantizeToCurrency(it.unit_price - sign * adj, exponent);
          }
        }
      }
    }
  } catch (e) {
    console.error("Pack rounding error (backend will handle):", e);
  }

  return result;
}

// Suma de los unit_price: es el importe que MercadoPago termina cobrando, porque
// el back pisa el `total` del payload con esta misma cuenta
// (MercadoPagoController::sumItems, que multiplica por quantity).
// El front siempre manda quantity 1, así que hoy da idéntico.
export const sumItems = (items) =>
  (items || []).reduce((s, it) => s + Number(it.unit_price || 0) * Number(it.quantity ?? 1), 0);

// ── Carrito ──
// `translate` se inyecta (en el componente es la t() de i18n). Si el namespace
// todavía no cargó, t() devuelve la clave y a MercadoPago le llega
// "illustrated_harmony" como descripción del item.
export function buildCartItems(selectedIds, priceMap, { language, translate } = {}) {
  const t = translate || ((k) => k);
  const prices = priceMap || {};
  return (selectedIds || [])
    .map((id) => {
      const meta = PRODUCT_META[id];
      if (!meta) return null;
      const price = prices[id] == null ? null : Number(prices[id]);
      return { id, nameKey: meta.nameKey, name: t(meta.nameKey), image: coverFor(id, language), price, color: meta.color };
    })
    .filter(Boolean);
}
